import React, { useState, useEffect } from "react";
import JoditEditor from "jodit-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaTrash, FaShareAlt, FaUpload } from "react-icons/fa";
import surveyService from "../../Services/surveyService";
import "../Survey/AddQuestions.css";
import { motion } from "framer-motion";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import NormalAlert from "../Alerts/NormalAlert";
import ConfirmationAlert from "../Alerts/ConfirmationAlert";

export default function AddQuestions({
  activeTab,
  setActiveTab,
  onOpenSidebar,
  isSidebarOpen,
  closeSidebar,
}) {
  const { assessmentUID } = useParams();
  const [surveyData, setSurveyData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newQuestion, setNewQuestion] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [assessmentID, setAssessmentID] = useState(null);
  const [questionHtml, setQuestionHtml] = useState("");
  const [caseStudyHtml, setCaseStudyHtml] = useState("");

  // Modal fields
  const [surveyName, setSurveyName] = useState("");
  const [description, setDescription] = useState("");
  const [minimumAnswers, setMinimumAnswers] = useState(0);
  const [shuffleQuestions, setShuffleQuestions] = useState(false);

  // Publish Survey
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [thankYouNote, setThankYouNote] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [minAnswersReq, setMinAnswersReq] = useState(0);
  //Edit Scheduled Survey
  const [ScheduledSurveyModal, setScheduledSurveyModal] = useState(false);
  const [days, setDays] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [error, setError] = useState("");
  //For Insights
  const [surveyStats, setSurveyStats] = useState(null);

  const [isExporting, setIsExporting] = useState(false);

  //Alert
  const [alert, setAlert] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  //Confirmation alert
  const [confirm, setConfirm] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const token = localStorage.getItem("JWTToken");
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  // Fetch survey & questions
  useEffect(() => {
    const fetchSurveyAndQuestions = async () => {
      if (!token) return;

      try {
        const surveyResponse = await surveyService.GetSurveyDetails({
          token: token,
          assessmentUID,
        });

        const surveys = surveyResponse.surveyDetails || [];
        if (surveyResponse.status === "Success" && surveys.length > 0) {
          console.log("Survey Details:", surveyResponse);
          const survey = surveys[0];
          setSurveyData(survey);
          setAssessmentID(survey.assessmentID);

          fetchQuestions(survey.assessmentID);
        } else {
          console.warn("Survey not found or empty response");
        }
      } catch (err) {
        console.error("Error fetching survey:", err);
      }
    };

    fetchSurveyAndQuestions();
  }, [assessmentUID]);

  const fetchQuestions = async (assessmentID) => {
    try {
      const questionResponse = await surveyService.GetQuestionsByAssessmentID({
        token: token,
        assessmentID,
      });

      if (questionResponse.status === "Success") {
        console.log("Added Questions: ", questionResponse);

        const questionsList = questionResponse.questions || [];

        setQuestions(
          questionsList.map((q) => ({
            questionID: q.questionID,
            text: q.question,
            questionUID: q.questionUID,
            questionSequence: q.questionSequence,
          }))
        );
      } else {
        setQuestions([]);
        console.warn("Failed to fetch questions:", questionResponse.message);
      }
    } catch (err) {
      console.error("Error fetching questions:", err);
      setQuestions([]);
    }
  };

  const fetchSurveyInsights = async () => {
    try {
      const payload = {
        token: token,
        assessmentUID: assessmentUID,
      };

      const response = await surveyService.GetSurveyData(payload);

      if (response.status === "Success" && response.surveyData?.length > 0) {
        console.log("Survey Data:", response.message);
        setSurveyStats(response.surveyData[0]);
      } else {
        setSurveyStats(null);
        console.warn("No survey data found.");
      }
    } catch (error) {
      console.error("Error fetching survey insights:", error);
      setSurveyStats(null);
    }
  };

  useEffect(() => {
    if (token && assessmentUID) {
      fetchSurveyInsights();
    }
  }, [assessmentUID, token]);

  const formatTime = (timeStr) => {
    if (!timeStr) return "0s";
    const [h, m, s] = timeStr.split(":").map(Number);
    let formatted = "";
    if (h) formatted += `${h}h `;
    if (m) formatted += `${m}m `;
    if (s) formatted += `${s}s`;
    return formatted.trim();
  };

  // handle export surver report
  const handleExportSurvey = async () => {
    const token = localStorage.getItem("JWTToken");
    const assessmentUID = window.location.pathname.split("/").pop();

    try {
      setIsExporting(true);

      const blob = await surveyService.ExportSurveyResponses({
        token,
        assessmentUID,
      });

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SurveyResponses_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "")}.xlsx`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showAlert("success", "Survey report downloaded successfully.");
    } catch (err) {
      console.error("Error exporting survey:", err);
      if (err?.response?.data?.message) {
        showAlert("error", err.response.data.message);
      } else {
        showAlert("error", "Failed to export survey report.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddClick = () => {
    setIsAdding(true);
    setNewQuestion("");
  };

  const handleCancel = () => {
    setIsAdding(false);
    setNewQuestion("");
  };

  const handleSave = async () => {
    if (!questionHtml || questionHtml.trim() === "" || !surveyData) return;

    try {
      const response = await surveyService.AddQuestion({
        token,
        question: questionHtml,
        assessmentID: surveyData.assessmentID,
      });

      if (response.status === "Success") {
        fetchQuestions(surveyData.assessmentID);
        setIsAdding(false);
        setQuestionHtml(""); // reset editor
      }
    } catch (err) {
      console.error("Error adding question:", err);
    }
  };

  const deleteQuestion = async (questionUID) => {
    if (!token || !surveyData) {
      showAlert("error", "Invalid user or survey data.");
      return;
    }
    try {
      const deleteData = {
        token: token,
        assessmentID: surveyData.assessmentID,
        questionUID: questionUID,
      };

      const res = await surveyService.DeleteQuestionCS(deleteData);

      if (res.status === "Success") {
        showAlert("success", res.message);
        fetchQuestions(surveyData.assessmentID);
      } else {
        showAlert("error", res.message || "Failed to delete question.");
      }
    } catch (err) {
      console.error("Failed to delete question:", err);
      showAlert(
        "error",
        err.message || "Something went wrong while deleting the question."
      );
    }
  };

  const handleDelete = (questionUID) => {
    setConfirm({
      show: true,
      title: "Delete Question?",
      message: "Are you sure you want to delete this question?",
      onConfirm: () => {
        setConfirm({ show: false });
        deleteQuestion(questionUID);
      },
    });
  };

  // Edit Survey Modal
  const openEditModal = React.useCallback(() => {
    if (!surveyData) return;

    setSurveyName(surveyData.assessmentName || "");
    setDescription(surveyData.description || "");
    setMinimumAnswers(surveyData.minimumAnswers || 0);

    const shuffleRaw =
      surveyData.shuffleQuestion ?? surveyData.shuffleQuestions;

    let shuffleValue = false;

    // Handle different backend formats
    if (
      shuffleRaw === true ||
      shuffleRaw === "true" ||
      shuffleRaw === 1 ||
      shuffleRaw === "1"
    ) {
      shuffleValue = true;
    } else if (
      shuffleRaw === false ||
      shuffleRaw === "false" ||
      shuffleRaw === 0 ||
      shuffleRaw === "0"
    ) {
      shuffleValue = false;
    }

    setShuffleQuestions(shuffleValue);

    // Convert total minutes
    if (surveyData.totalTimeForSurvey) {
      const totalMins = parseInt(surveyData.totalTimeForSurvey);
      setDays(Math.floor(totalMins / (24 * 60)));
      setHours(Math.floor((totalMins % (24 * 60)) / 60));
      setMinutes(totalMins % 60);
    } else {
      setDays(0);
      setHours(0);
      setMinutes(0);
    }

    // Case study
    setCaseStudyHtml(surveyData.caseStudy || "");

    setIsEditModalOpen(true);
  }, [surveyData]);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(questions);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);

    const updatedQuestions = reordered.map((q, index) => ({
      questionID: q.questionID, //  now correct
      questionSequence: index + 1,
    }));

    setQuestions(reordered);

    const payload = {
      token: localStorage.getItem("JWTToken"),
      assessmentID: surveyData.assessmentID,
      updateQuestions: JSON.stringify(updatedQuestions),
    };

    console.log(" Final Payload Sent to API:", payload);

    try {
      const res = await surveyService.UpdateQuestionSeq(payload);
      if (res.status === "Success") {
        console.log(res.message);
      } else {
        showAlert("error", res.message || "Failed to update question sequence");
      }
    } catch (error) {
      console.error("❌ Error updating question sequence:", error);
    }
  };

  //handle Activate Survey
  const handleActivateSurvey = async () => {
    try {
      const payload = {
        token: token,
        assessmentUID: assessmentUID,
      };

      const response = await surveyService.ActivateSurvey(payload);

      if (response.status === "Success") {
        showAlert("success", response.message);
        setTimeout(() => {
          navigate("/survey-list", { state: { defaultTab: "Active" } });
        }, 3000);
      } else {
        showAlert("error", response.message || "Failed to activate Survey");
      }
    } catch (err) {
      console.error("Error activating survey:", err);
      showAlert("error", "Something went wrong while activating the survey.");
    }
  };

  //handle Activate Survey
  const handleDeactivateSurvey = async () => {
    try {
      const payload = {
        token: token,
        assessmentUID: assessmentUID,
      };

      const response = await surveyService.DeactivateSurvey(payload);

      if (response.status === "Success") {
        showAlert("success", response.message);
        setTimeout(() => {
          navigate("/survey-list", { state: { defaultTab: "Inactive" } });
        }, 3000);
      } else {
        showAlert("error", response.message || "Failed to activate Survey");
      }
    } catch (err) {
      console.error("Error activating survey:", err);
      showAlert("error", "Something went wrong while activating the survey.");
    }
  };

  const [caseStudyBlobURL, setCaseStudyBlobURL] = useState(null);

  useEffect(() => {
    if (!surveyData?.caseStudy || !token) return;

    const fetchPDF = async () => {
      const fileName = surveyData.caseStudy.split("/").pop();

      try {
        const response = await surveyService.GetCaseStudy(fileName, token);

        // Convert ArrayBuffer or raw response to Blob
        const pdfBlob = new Blob([response], { type: "application/pdf" });

        const blobURL = URL.createObjectURL(pdfBlob);
        setCaseStudyBlobURL(blobURL);
      } catch (err) {
        console.error("Error fetching case study PDF:", err);
        setCaseStudyBlobURL(null);
      }
    };

    fetchPDF();

    // Cleanup old Blob URL on unmount or when survey changes
    return () => {
      if (caseStudyBlobURL) URL.revokeObjectURL(caseStudyBlobURL);
    };
  }, [surveyData]);

  if (!surveyData)
    return <p className="text-center mt-10">Loading survey data...</p>;

  return (
    <>
      {activeTab === "Insights" && (
        <div className="flex flex-col items-center justify-center min-h-[70vh] bg-[#f1f6f3] rounded-lg shadow-inner p-10">
          <h2 className="text-2xl font-bold text-[#3bb290] mb-10 text-center">
            Ongoing Status
          </h2>

          {!surveyStats ? (
            <p className="text-gray-500 text-lg">Loading survey data...</p>
          ) : (
            <div className="grid grid-cols-6 gap-8 w-full max-w-7xl text-center">
              {/* Total Responder */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-[#3bb290]">
                  {surveyStats.totalResponders ?? 0}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base">
                  Total Responders
                </span>
              </div>

              {/* Delivered */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-[#3bb290]">
                  {surveyStats.delivered ?? 0}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base">
                  Delivered
                </span>
              </div>

              {/* Started */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-[#3bb290]">
                  {surveyStats.started ?? 0}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base">
                  Started
                </span>
              </div>

              {/* Completed */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-[#3bb290]">
                  {surveyStats.completed ?? 0}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base">
                  Completed
                </span>
              </div>

              {/* Response Captured */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold text-[#3bb290]">
                  {surveyStats.capturedResponse ?? 0}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base">
                  Response Captured
                </span>
              </div>

              {/* Avg. Completion Time */}
              <div className="bg-white shadow-sm rounded-2xl p-6 flex flex-col items-center justify-center">
                <span className="text-2xl font-extrabold text-[#3bb290]">
                  {formatTime(surveyStats.averageCompletionTime)}
                </span>
                <span className="mt-2 text-gray-700 font-semibold text-base text-center">
                  Avg. Completion Time
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "Reports" && (
        <div className="flex flex-col items-center justify-start min-h-[70vh] bg-white rounded-lg shadow p-10">
          {surveyData.displayStatus === "Active" ||
          surveyData.displayStatus === "Completed" ? (
            <button
              className={`mt-4 flex items-center gap-2 px-6 py-2 rounded transition
              ${
                isExporting
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : "border border-[#3bb290] text-[#3bb290] hover:bg-[#3bb290] hover:text-white"
              }
            `}
              onClick={handleExportSurvey}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <span className="h-4 w-4 border-2 border-t-transparent rounded-full animate-spin"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FaUpload />
                  Export Survey Report
                </>
              )}
            </button>
          ) : (
            <div className="mt-4 text-center">
              <p className="text-lg font-semibold text-gray-600">
                The report will be generated after the survey end.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "Survey" && (
        <div className="min-h-screen bg-gray-50 flex flex-col py-2 px-4 w-full">
          <div className="w-full bg-white shadow-lg rounded-none md:rounded-2xl p-8 flex flex-col md:flex-row gap-8 h-[calc(100vh-4rem)]">
            {/* Case Study Left Panel */}
            <div className="flex-[1.3] border-r border-gray-200 pr-6 flex flex-col">
              <div className="sticky top-[64px] bg-white z-20 border-b pb-2 flex justify-between items-center">
                <h2 className="text-xl font-bold mt-3">
                  Survey: {surveyData.assessmentName}
                </h2>
                <div className="flex gap-3">
                  {/* {surveyData?.displayStatus === "Draft" && (
                  <button
                    onClick={openEditModal}
                    className="px-4 py-2 bg-[#3bb290] rounded-md text-white font-medium"
                  >
                    Edit Survey
                  </button>
                  )} */}
                  {surveyData.statusFlag === 0 && (
                    <button
                      onClick={() => setShowPublishModal(true)}
                      className="p-2 rounded-full text-[#3bb290] hover:bg-[#e6f7f2]"
                      title="Publish Survey"
                    >
                      <FaUpload size={18} />
                    </button>
                  )}
                </div>
              </div>

              <div className="overflow-y-auto flex-1 mt-2">
                <h3 className="font-semibold text-gray-700 mb-2">Case Study</h3>
                <div className="border p-4 pt-0 rounded-lg min-h-[400px] bg-gray-50">
                  {caseStudyBlobURL ? (
                    <iframe
                      src={`${caseStudyBlobURL}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-width`}
                      title="Case Study PDF"
                      width="100%"
                      height="100%"
                      style={{ minHeight: "500px" }}
                    />
                  ) : (
                    <p className="text-gray-500">No case study available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Questions Right Panel */}
            <div className="flex-[0.7] pl-6 flex flex-col min-h-0">
              <div className="sticky top-[64px] bg-white z-20 border-b pb-2 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-700">
                  {isAdding ? "Add Questions" : "Questions"}
                </h3>
                {surveyData?.displayStatus === "Draft" && (
                  <button
                    onClick={handleAddClick}
                    className="px-4 py-2 border border-gray-400 rounded-md text-gray-700 font-medium hover:bg-gray-100"
                  >
                    Add Question
                  </button>
                )}
              </div>

              <div className="mt-4 flex-1 overflow-hidden">
                <div className="questions">
                  {isAdding ? (
                    <div>
                      <h4 className="text-gray-700 font-medium mb-2">
                        Question No. {questions.length + 1}
                      </h4>

                      {/* Rich Text Editor */}
                      <div className="border border-gray-300 rounded-lg bg-white min-h-[390px]">
                        <JoditEditor
                          value={questionHtml}
                          config={{
                            height: 390,
                            buttons:
                              "bold,italic,underline,|,ul,ol,|,table,link,|,undo,redo",
                            showCharsCounter: false,
                            showWordsCounter: false,
                            showXPathInStatusbar: false,
                          }}
                          onBlur={(content) => setQuestionHtml(content)}
                        />
                      </div>

                      <div className="flex justify-end gap-4 mt-6">
                        <button
                          className="px-6 py-2 border border-gray-400 rounded-md font-semibold text-gray-700 hover:bg-gray-100"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          className="px-6 py-2 bg-[#3bb290] text-white rounded-md font-semibold hover:bg-[#339c7c]"
                        >
                          Add Question
                        </button>
                      </div>
                    </div>
                  ) : questions.length === 0 ? (
                    <p className="text-gray-400 italic">Add Question</p>
                  ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="questions">
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="space-y-3"
                          >
                            {questions.map((q, index) => (
                              <Draggable
                                key={q.questionUID}
                                draggableId={q.questionUID}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                    className="flex items-start border rounded-md bg-white shadow-sm"
                                  >
                                    {/* DRAG HANDLE */}
                                    <div
                                      {...provided.dragHandleProps}
                                      className="px-3 py-2 bg-gray-100 font-medium text-gray-600 border-r cursor-grab select-none"
                                    >
                                      Q.{index + 1}
                                    </div>

                                    {/* CONTENT */}
                                    <div className="flex items-start justify-between w-full px-4 py-2">
                                      <div
                                        className="flex-1 prose prose-sm max-w-none text-gray-700 pr-4"
                                        dangerouslySetInnerHTML={{
                                          __html: q.text,
                                        }}
                                      />

                                      <button
                                        onClick={() =>
                                          handleDelete(q.questionUID)
                                        }
                                        className="flex-shrink-0 text-red-600 hover:text-red-800 mt-1"
                                        title="Delete Question"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Update Survey Modal */}
          {isEditModalOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="w-full max-w-5xl bg-white/95 backdrop-blur-lg shadow-2xl rounded-3xl p-12 border border-gray-200 relative"
              >
                {/* Close Button */}
                <button
                  onClick={closeEditModal}
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>

                {/* Header */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-extrabold text-[#3bb290] mb-2">
                    {surveyName || "Edit Survey"}
                  </h1>
                  <p className="text-gray-500 text-lg">
                    Modify the details below to update your survey
                  </p>
                </div>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const totalMinutes =
                      parseInt(days || 0) * 24 * 60 +
                      parseInt(hours || 0) * 60 +
                      parseInt(minutes || 0);
                    const totalTimeString = `${totalMinutes} Minutes`;

                    try {
                      const result = await surveyService.UpdateSurveyCS({
                        token: localStorage.getItem("JWTToken"),
                        assessmentUID: assessmentUID,
                        surveyName,
                        surveyDescription: description,
                        minimumAnswers: minimumAnswers.toString(),
                        totalTimeForSurvey: totalTimeString,
                        caseStudy: caseStudyHtml,
                        shuffleQuestion: shuffleQuestions,
                      });

                      if (result.status === "Success") {
                        showAlert("success", result.message);
                        const updatedSurvey =
                          await surveyService.GetSurveyDetails({
                            token: localStorage.getItem("JWTToken"),
                            assessmentUID: assessmentUID,
                          });
                        if (
                          updatedSurvey.status === "Success" &&
                          updatedSurvey.surveyDetails?.length
                        ) {
                          setSurveyData(updatedSurvey.surveyDetails[0]);
                          fetchQuestions(
                            updatedSurvey.surveyDetails[0].assessmentID
                          );
                        }
                        closeEditModal();
                      } else {
                        showAlert(
                          "error",
                          result.message || "Failed to update survey."
                        );
                      }
                    } catch (err) {
                      console.error(err);
                      showAlert(
                        "error",
                        "Something went wrong while updating survey."
                      );
                    }
                  }}
                  className="space-y-8 max-h-[70vh] overflow-y-auto px-4"
                >
                  {/* Survey Name */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Survey Name
                    </label>
                    <input
                      type="text"
                      value={surveyName}
                      onChange={(e) => setSurveyName(e.target.value)}
                      placeholder="Enter Survey Name"
                      className="w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter Description"
                      rows="4"
                      className="w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all resize-none"
                    />
                  </div>

                  {/* Case Study */}
                  <div className="px-1">
                    <label className="block text-gray-700 font-semibold mb-2">
                      Case Study
                    </label>
                    <div className="border border-gray-300 rounded-xl overflow-hidden hover:border-[#3bb290] transition-shadow shadow-sm">
                      <div className="px-5 py-4">
                        <JoditEditor
                          value={caseStudyHtml}
                          config={{
                            height: 250,
                            buttons:
                              "bold,italic,underline,|,ul,ol,|,table,link,|,undo,redo",
                          }}
                          onBlur={(content) => setCaseStudyHtml(content)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Minimum Answers */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Minimum Answers
                    </label>
                    <input
                      type="number"
                      value={minimumAnswers}
                      min={0}
                      onChange={(e) => setMinimumAnswers(e.target.value)}
                      className="w-full border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all"
                    />
                  </div>

                  {/* Shuffle Questions */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">
                      Shuffle Questions
                    </label>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="shuffleQuestions"
                          value="true"
                          checked={shuffleQuestions === true}
                          onChange={() => setShuffleQuestions(true)}
                          className="h-4 w-4 text-[#3bb290] focus:ring-[#3bb290]"
                        />
                        <span className="text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="shuffleQuestions"
                          value="false"
                          checked={shuffleQuestions === false}
                          onChange={() => setShuffleQuestions(false)}
                          className="h-4 w-4 text-[#3bb290] focus:ring-[#3bb290]"
                        />
                        <span className="text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Total Time */}
                  <div>
                    <label className="text-gray-700 font-semibold mb-2">
                      Total Time For Survey
                    </label>
                    <div className="flex space-x-4">
                      <div>
                        <label>Days</label>
                        <input
                          type="number"
                          value={days}
                          onChange={(e) => setDays(e.target.value)}
                          className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all"
                          min="0"
                        />
                      </div>
                      <div>
                        <label>Hours</label>
                        <input
                          type="number"
                          value={hours}
                          onChange={(e) => setHours(e.target.value)}
                          className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all"
                          min="0"
                          max="23"
                        />
                      </div>
                      <div>
                        <label>Minutes</label>
                        <input
                          type="number"
                          value={minutes}
                          onChange={(e) => setMinutes(e.target.value)}
                          className="w-full mt-2 border border-gray-300 rounded-xl px-5 py-4 focus:outline-none focus:ring-2 focus:ring-[#3bb290] transition-all"
                          min="0"
                          max="59"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-center mt-8 space-x-4">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={closeEditModal}
                      className="bg-gray-300 text-gray-800 font-bold px-12 py-4 rounded-2xl shadow-md hover:shadow-xl transition-transform"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      className="bg-gradient-to-r from-[#3bb290] to-[#1f7d6b] text-white font-bold px-12 py-4 rounded-2xl shadow-xl hover:shadow-2xl transition-transform"
                    >
                      Save Changes
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}

          {/* Publish Survey Modal */}
          {showPublishModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 relative"
              >
                {/* Close Button */}
                <button
                  onClick={() => setShowPublishModal(false)}
                  className="absolute top-3 right-4 text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>

                <h2 className="text-xl font-bold text-[#3bb290] mb-4">
                  Publish Survey
                </h2>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();

                    // Validation Checks
                    if (!thankYouNote.trim()) {
                      setError("Thank You Note is required.");
                      return;
                    }
                    if (!startDateTime || !endDateTime) {
                      setError("Please select both Start and End Date & Time.");
                      return;
                    }
                    if (new Date(endDateTime) <= new Date(startDateTime)) {
                      setError(
                        "End Date and Time must be greater than Start Date and Time."
                      );
                      return;
                    }
                    if (minAnswersReq < 0) {
                      setError("Minimum answers required cannot be negative.");
                      return;
                    }

                    setError(""); // clear error if valid

                    try {
                      const payload = {
                        token: localStorage.getItem("JWTToken"),
                        assessmentUID: assessmentUID,
                        thankYouNote,
                        startDateTime,
                        endDateTime,
                        minAnswersReq: Number(minAnswersReq),
                      };

                      const res = await surveyService.PublishSurvey(payload);

                      if (res.status === "Success") {
                        showAlert("success", res.message);
                        setTimeout(() => {
                          navigate("/survey-list");
                        }, 3000);
                        setSurveyData((prev) => ({ ...prev, statusFlag: 1 }));
                        setShowPublishModal(false);
                      } else {
                        showAlert(
                          "error",
                          res.message || "Failed to publish survey."
                        );
                      }
                    } catch (err) {
                      console.error(err);
                      showAlert(
                        "error",
                        "Something went wrong while publishing survey."
                      );
                    }
                  }}
                  className="space-y-4"
                >
                  {/* Thank You Note */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Thank You Note
                    </label>
                    <textarea
                      value={thankYouNote}
                      onChange={(e) => setThankYouNote(e.target.value)}
                      placeholder="Enter thank you message..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* Start DateTime */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Start Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={startDateTime}
                      onChange={(e) => setStartDateTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* End DateTime */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      End Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* Minimum Answer Required */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Minimum Answers
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={minAnswersReq}
                      onChange={(e) => setMinAnswersReq(e.target.value)}
                      placeholder="Enter Minimum Answer Required"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* Error Message (in red) */}
                  {error && (
                    <p className="text-red-500 text-sm font-medium mt-2">
                      {error}
                    </p>
                  )}
                  {/* Buttons */}
                  <div className="flex justify-end gap-4 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowPublishModal(false)}
                      className="px-5 py-2 border rounded-lg"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      className="px-5 py-2 bg-[#3bb290] text-white rounded-lg"
                    >
                      Publish
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}

          {/* Edit Scheduled Survey Modal */}
          {ScheduledSurveyModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 relative"
              >
                {/* Close Button */}
                <button
                  onClick={() => setScheduledSurveyModal(false)}
                  className="absolute top-3 right-4 text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>

                <h2 className="text-xl font-bold text-[#3bb290] mb-4">
                  Edit Scheduled Survey
                </h2>

                <form
                  onSubmit={async (e) => {
                    e.preventDefault();

                    if (!startDateTime || !endDateTime) {
                      setError("Please select both Start and End Date & Time.");
                      return;
                    }
                    if (new Date(endDateTime) <= new Date(startDateTime)) {
                      setError(
                        "End Date and Time must be greater than Start Date and Time."
                      );
                      return;
                    }

                    setError("");

                    try {
                      const payload = {
                        token: localStorage.getItem("JWTToken"),
                        assessmentUID: assessmentUID,
                        startDateTime,
                        endDateTime,
                      };

                      const res = await surveyService.EditScheduledSurvey(
                        payload
                      );

                      if (res.status === "Success") {
                        showAlert("success", res.message);
                        setTimeout(() => {
                          navigate("/survey-list");
                        }, 3000);
                        setSurveyData((prev) => ({ ...prev, statusFlag: 1 }));
                        setScheduledSurveyModal(false);
                      } else {
                        setError(res.message || "Failed to publish survey.");
                      }
                    } catch (err) {
                      console.error(err);
                      setError("Something went wrong while publishing survey.");
                    }
                  }}
                  className="space-y-4"
                >
                  {/* Start DateTime */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Start Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={startDateTime}
                      onChange={(e) => setStartDateTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* End DateTime */}
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      End Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    />
                  </div>

                  {/* Error Message (in red) */}
                  {error && (
                    <p className="text-red-500 text-sm font-medium mt-2">
                      {error}
                    </p>
                  )}
                  {/* Buttons */}
                  <div className="flex justify-end gap-4 mt-4">
                    <button
                      type="button"
                      onClick={() => setScheduledSurveyModal(false)}
                      className="px-5 py-2 border rounded-lg"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      className="px-5 py-2 bg-[#3bb290] text-white rounded-lg"
                    >
                      Schedule Survey
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 z-50 ${
          isSidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Close Button */}
        <div className="flex justify-end p-4 border-b">
          <button
            onClick={closeSidebar}
            className="text-gray-600 hover:text-gray-900 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto h-full">
          <h3 className="text-center text-[#3bb290] text-lg font-bold mb-4">
            Survey Details
          </h3>

          <div className="space-y-3">
            {surveyData?.displayStatus !== "Completed" &&
              surveyData?.displayStatus !== "Draft" &&
              (surveyData?.displayStatus === "Inactive" ? (
                <button
                  onClick={handleActivateSurvey}
                  className="w-full bg-green-100 py-3 rounded-md hover:bg-green-200 font-medium text-green-700"
                >
                  Activate Survey
                </button>
              ) : (
                <button
                  onClick={handleDeactivateSurvey}
                  className="w-full bg-red-100 py-3 rounded-md hover:bg-red-200 font-medium text-red-700"
                >
                  Deactivate Survey
                </button>
              ))}

            {/* Links */}
            <button className="w-full bg-gray-100 py-2 rounded-md hover:bg-gray-200 font-medium">
              Add Permission
            </button>

            {surveyData?.displayStatus !== "Scheduled" && (
              <button
                className="w-full bg-gray-100 py-2 rounded-md hover:bg-gray-200 font-medium"
                onClick={() => navigate(`/Manage-Responder/${assessmentUID}`)}
              >
                Manage Responder
              </button>
            )}

            <button
              className="w-full bg-gray-100 py-2 rounded-md hover:bg-gray-200 font-medium"
              onClick={() => setScheduledSurveyModal(true)}
            >
              Edit Survey Schedule
            </button>

            <button className="w-full bg-gray-100 py-2 rounded-md hover:bg-gray-200 font-medium">
              Copy Survey
            </button>

            {surveyData?.displayStatus === "Active" && (
              <div className="text-right mt-4">
                <button className="bg-[#3bb290] text-white px-4 py-2 rounded-md hover:bg-[#2f9c7c]">
                  End Survey
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Overlay */}
      {isSidebarOpen && (
        <div
          onClick={closeSidebar}
          className="fixed inset-0 bg-black bg-opacity-30 z-40"
        ></div>
      )}

      <NormalAlert
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ ...alert, show: false })}
      />

      <ConfirmationAlert
        show={confirm.show}
        title={confirm.title}
        message={confirm.message}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onCancel={() => setConfirm({ show: false })}
        onConfirm={confirm.onConfirm}
      />
    </>
  );
}
